import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Ban, CheckCheck, CircleCheckBig, CircleX, Download, Eye, Pencil, Printer, RotateCcw, Search, SearchCheck } from 'lucide-react';
import * as hr from '../../api';
import { hrSubUrl } from '../../catalog';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { Select } from '../../components/Controls';
import { FileChip, openStored } from '../../components/Files';
import { download, fmtMoney, toCSV } from '../../format';
import { Empty, esc, findType, kNaira, PriorityChip, printHtml, StatusChip, STATUS_LABEL } from './shared';
import Loader from '../../components/Loader';

// HR Request Center > All (Stephanet's custom/hrrc/AllRequests.jsx): every request you may see, with search, status / module
// filters, print, export and the request details. Approve / Send Back / Reject act on the request's approval — its own, or
// the leave / correction / HR case it created — for whoever holds the current step.

const COLS = [['Request No', r => r.request_no], ['Title', r => r.title], ['Module', r => r.module_label], ['Type', r => r.request_type],
  ['Requested By', r => r.requested_by], ['Department', r => r.department], ['Amount', r => r.amount ?? ''], ['Priority', r => r.priority],
  ['Status', r => STATUS_LABEL[r.status]], ['Date', r => r.date], ['Due Date', r => r.due_date || ''], ['Overdue', r => (r.overdue ? 'Yes' : 'No')]];

export default function AllRequests({ meta }) {
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(params.get('status') || 'All');
  const [module, setModule] = useState('All');
  const [open, setOpen] = useState(params.get('open') || null);
  const load = () => hr.hrrcList().then(setD).catch(() => setD({ rows: [], total: 0, statuses: [], can: {} }));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => {
    if (!d) return [];
    const s = q.trim().toLowerCase();
    return d.rows.filter(r => (!s || (r.title || '').toLowerCase().includes(s) || r.request_no.toLowerCase().includes(s) || (r.requested_by || '').toLowerCase().includes(s))
      && (status === 'All' || (status === 'overdue' ? r.overdue : r.status === status)) && (module === 'All' || r.module === module));
  }, [d, q, status, module]);
  if (!d) return <Loader title="Loading requests…" />;
  const print = () => printHtml('Requests', `<h1>HR Request Center — Requests</h1><p class="muted">${status === 'All' ? 'All statuses' : esc(STATUS_LABEL[status] || 'Overdue')} · ${rows.length} request(s)</p>
    <table><thead><tr>${COLS.slice(0, 10).map(([h]) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map(r => `<tr>${COLS.slice(0, 10).map(([h, get]) =>
      `<td>${h === 'Amount' ? (r.amount ? fmtMoney(r.amount) : '—') : esc(get(r) || '—')}</td>`).join('')}</tr>`).join('')}</tbody></table>`);
  return (
    <>
      <div className="ts-filters hrc-filters no-print">
        <div className="mdr-search"><Search size={14} /><input placeholder="Search request no, title..." value={q} onChange={e => setQ(e.target.value)} /></div>
        <Select value={status} onChange={e => setStatus(e.target.value)} title="Status">
          <option value="All">All Status</option>{d.statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}<option value="overdue">Overdue</option>
        </Select>
        <Select value={module} onChange={e => setModule(e.target.value)} title="Module">
          <option value="All">All Modules</option>{meta.modules.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
        </Select>
        <button className="mdr-btn" onClick={print} disabled={!rows.length}><Printer size={14} /> Print</button>
        <button className="mdr-btn" disabled={!rows.length} onClick={() => download('requests.csv', toCSV(COLS.map(([l, get]) => ({ label: l, get })), rows))}><Download size={14} /> Export</button>
      </div>
      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>{['Request No', 'Title', 'Module', 'Type', 'Requested By', 'Department', 'Amount', 'Priority', 'Status', 'Date', 'Action'].map(h => <th key={h} className={h === 'Action' ? 'c' : ''}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className={`mdr-row ${r.overdue ? 'hrc-overdue' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  <td className="mono">{r.request_no}</td>
                  <td><b>{r.title || '—'}</b>{r.overdue && <small className="hrc-od">Overdue · due {r.due_date}</small>}</td>
                  <td>{r.module_label}</td><td className="small">{r.request_type}</td><td>{r.requested_by}</td><td>{r.department || '—'}</td>
                  <td className="b nowrap">{kNaira(r.amount)}</td><td><PriorityChip p={r.priority} /></td><td><StatusChip s={r.status} /></td>
                  <td className="nowrap">{r.date}</td>
                  <td className="c"><button className="mds-view" onClick={() => setOpen(r.id)} title="View"><Eye size={13} /></button></td>
                </tr>
              ))}
              {!rows.length && <Empty cols={11}>{d.rows.length ? 'No requests match.' : 'No requests yet.'}</Empty>}
            </tbody>
          </table>
        </div>
        <div className="mdr-foot hrc-foot"><span>Showing {rows.length} of {d.total} requests</span></div>
      </div>
      {open && <RequestView id={open} meta={meta} onClose={() => { setOpen(null); if (params.get('open')) setParams({}); }} onChanged={load} />}
    </>
  );
}

const ACTION_UI = {
  review: ['Take into Review', SearchCheck, 'b-blue'], approve: ['Approve', CircleCheckBig, 'b-green'], send_back: ['Send Back', RotateCcw, 'b-orange'],
  reject: ['Reject', CircleX, 'b-red'], complete: ['Mark Completed', CheckCheck, 'b-green'], cancel: ['Cancel Request', Ban, 'b-outline'],
};
const STATE_TEXT = { approved: s => `Approved · ${s.by || '—'}`, current: s => `Awaiting decision${s.with ? ` · ${s.with}` : ''}`, rejected: () => 'Rejected here',
  sent_back: () => 'Sent back here', waiting: () => 'Waiting' };

export function RequestView({ id, meta, onClose, onChanged }) {
  const { notify } = useHR();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);
  const load = () => hr.hrrcGet(id).then(setR).catch(e => { notify(e.message, 'bad'); onClose(); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return <Modal title="Request Details" onClose={onClose}><Loader title="Loading…" /></Modal>;
  const type = findType(meta, r.type_id);
  const show = k => {
    const v = r.details[k];
    const f = type?.fields.find(x => x.key === k);
    if (f?.kind === 'employee') return meta.employees.find(e => String(e.id) === String(v))?.name || v;
    if (f?.kind === 'department') return meta.departments.find(x => String(x.id) === String(v))?.name || v;
    return String(v);
  };
  const fields = (type?.fields || []).filter(f => f.kind !== 'file' && r.details[f.key] !== undefined && r.details[f.key] !== '');
  const act = async (action, comment) => {
    setBusy(true);
    try { const x = await hr.hrrcAction(r.id, { action, comment }); notify(x.message); setAsk(null); load(); onChanged?.(); }
    catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const print = () => printHtml(r.request_no, `<h1>HR REQUEST — ${esc(r.request_no)}</h1><p class="muted">${esc(r.module_name)} · ${esc(r.request_type)} · Priority ${esc(r.priority)} · ${esc(STATUS_LABEL[r.status])}</p>
    <h2>Request</h2><div class="grid">${[['Title', r.title], ['Requested By', r.requested_by], ['Department', r.department], ['Project / Site', r.project_site],
      ['Amount', r.amount ? fmtMoney(r.amount) : '—'], ['Date', r.date], ['Required Date', r.required_date], ['SLA', r.sla_text], ['Due Date', r.due_date]]
      .map(([k, v]) => `<div><div class="l">${k}</div><div class="v">${esc(v || '—')}</div></div>`).join('')}</div>
    <h2>Details</h2><div class="grid">${fields.map(f => `<div><div class="l">${esc(f.label)}</div><div class="v">${esc(show(f.key))}</div></div>`).join('')}</div>
    <h2>Approval Route</h2><table><tr><th>Level</th><th>Approver</th><th>Status</th><th>By</th><th>Date</th></tr>${r.route.map(s => `<tr><td>${s.level}</td><td>${esc(s.role)}</td><td>${esc(s.state)}</td><td>${esc(s.by || '')}</td><td>${esc((s.at || '').replace('T', ' ').slice(0, 16))}</td></tr>`).join('')}</table>
    <div class="sign"><div>Requested By<br/>${esc(r.requested_by)}</div>${r.route.map(s => `<div>${esc(s.role)}</div>`).slice(0, 3).join('')}</div>`);
  return (
    <Modal wide className="hrc-view" title="Request Details" onClose={onClose}
           footer={<>
             {r.actions.map(a => {
               const [txt, Ico, cls] = ACTION_UI[a];
               return <button key={a} className={`mdr-bulk-btn ${cls}`} disabled={busy} onClick={() => (['reject', 'send_back'].includes(a) ? setAsk(a) : act(a))}><Ico size={13} /> {txt}</button>;
             })}
             {r.editable && <button className="mdr-bulk-btn b-outline" onClick={() => navigate(hrSubUrl('hrrc-new', { draft: r.id }))}><Pencil size={13} /> Edit &amp; Resubmit</button>}
             <span className="wh-spacer" />
             <button className="mdr-btn" onClick={onClose}>Close</button>
             <button className="mds-run" onClick={print}><Printer size={14} /> Print</button>
           </>}>
      <div className="hrc-kv">
        {[['Request No', r.request_no], ['Title', r.title], ['Module', r.module_name], ['Type', r.request_type], ['Requested By', r.requested_by], ['Department', r.department || '—']]
          .map(([k, v]) => <div key={k}><small>{k}</small><b>{v || '—'}</b></div>)}
        {r.employee && <div><small>Employee</small><b>{r.employee}{r.employee_no ? ` (${r.employee_no})` : ''}</b></div>}
        <div><small>Priority</small><b><PriorityChip p={r.priority} /></b></div>
        <div><small>Status</small><b><StatusChip s={r.status} /></b></div>
        <div><small>Amount</small><b>{r.amount ? fmtMoney(r.amount) : '—'}</b></div>
        <div><small>Date</small><b>{r.date}</b></div>
        <div><small>SLA</small><b>{r.sla_text}</b></div>
        <div><small>Due Date</small><b className={r.overdue ? 'hrc-od' : ''}>{r.due_date || '—'}{r.overdue ? ' (overdue)' : ''}</b></div>
        {r.project_site && <div><small>Project / Site</small><b>{r.project_site}</b></div>}
        {r.required_date && <div><small>Required Date</small><b>{r.required_date}</b></div>}
        {r.home_record && <div><small>Created</small><b>{r.home_record.reference} · {r.home_record.where}</b></div>}
      </div>
      {r.home_record && r.status === 'sent_back' && <p className="hrc-warn">Sent back: correct it in {r.home_record.where} ({r.home_record.reference}), then resubmit.</p>}

      {r.route.length > 0 && <>
        <h4 className="wh-sub">Approval Route</h4>
        <div className="hrc-track">{r.route.map(s => (
          <div key={s.level} className={`hrc-track-step s-${s.state}`}><span>{s.level}</span><b>{s.role}</b><small>{(STATE_TEXT[s.state] || STATE_TEXT.waiting)(s)}</small></div>))}
        </div></>}

      {fields.length > 0 && <><h4 className="wh-sub">Request Details</h4>
        <div className="hrc-kv">{fields.map(f => <div key={f.key}><small>{f.label}</small><b>{show(f.key)}</b></div>)}</div></>}

      {r.files.length > 0 && <><h4 className="wh-sub">Attachments</h4>
        <div className="hrc-files">{r.files.map(f => <FileChip key={f.id} name={f.name} size={f.size} onOpen={() => openStored(() => hr.hrrcFileBlob(r.id, f.id))} />)}</div></>}

      <h4 className="wh-sub">History</h4>
      <ul className="ts-hist">{r.events.map((e, i) => <li key={i}><b>{e.action}</b> · {e.by} · {e.at}{e.from || e.to ? <> · {e.from || '—'} → {e.to}</> : null}{e.comment && <><br /><small>{e.comment}</small></>}</li>)}</ul>

      {ask && <CommentModal title={ask === 'reject' ? `Reject ${r.request_no}` : `Send back ${r.request_no}`} busy={busy} onClose={() => setAsk(null)}
                            placeholder={ask === 'reject' ? 'Why is the request rejected?' : 'What must the requester correct?'} onSubmit={c => act(ask, c)} />}
    </Modal>
  );
}

export function CommentModal({ title, placeholder, busy, onClose, onSubmit, button = 'Confirm' }) {
  const [c, setC] = useState('');
  return (
    <Modal title={title} onClose={onClose}>
      <form className="mdr-form" onSubmit={e => { e.preventDefault(); if (c.trim()) onSubmit(c.trim()); }}>
        <label className="mdh-field">Comment<textarea rows={3} autoFocus value={c} onChange={e => setC(e.target.value)} placeholder={placeholder} /></label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="mds-run" disabled={busy || !c.trim()}>{button}</button>
        </div>
      </form>
    </Modal>
  );
}
